import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { BehaviorSubject, Observable } from 'rxjs';

import { BiometricService } from './biometric.service';
import { SecurityService } from '../shared/security/security.service';

/**
 * Bloqueia o app exigindo biometria ao abrir e ao voltar do background,
 * quando há sessão ativa e a biometria está habilitada.
 */
@Injectable({ providedIn: 'root' })
export class AppLockService {
  private locked = new BehaviorSubject<boolean>(false);
  private prompting = false;
  private initialized = false;
  // "Armado" = o app foi para o background de verdade (usuário saiu), então
  // ao voltar deve bloquear. NÃO arma quando o background foi causado pelo
  // próprio diálogo de biometria — isso evita o loop de re-bloqueio.
  private armed = false;

  constructor(
    private biometricService: BiometricService,
    private securityService: SecurityService
  ) {}

  get locked$(): Observable<boolean> {
    return this.locked.asObservable();
  }

  /**
   * Inicia o monitoramento de foreground/background e avalia o bloqueio
   * inicial (cold start). Deve ser chamado após a sessão de segurança carregar.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        // Foi para o background. Só arma se NÃO for o diálogo de biometria
        // (nosso, do app-lock, ou o do login) que tirou o app do foreground.
        if (!this.biometricService.verifying) {
          this.armed = true;
        }
      } else if (this.armed) {
        // Voltou de um background real → bloqueia.
        this.armed = false;
        this.lockIfNeeded();
      }
    });

    // Bloqueio na abertura do app (cold start).
    this.lockIfNeeded();
  }

  /** Bloqueia (e dispara o desbloqueio) se houver sessão e biometria habilitada. */
  async lockIfNeeded(): Promise<void> {
    if (this.locked.value) {
      this.promptUnlock();
      return;
    }

    if (!this.securityService.valid) {
      return;
    }

    if (!(await this.biometricService.isEnabled())) {
      return;
    }

    this.locked.next(true);
    this.promptUnlock();
  }

  /** Dispara o prompt nativo de biometria para desbloquear. */
  async promptUnlock(): Promise<void> {
    if (this.prompting || !this.locked.value) {
      return;
    }

    this.prompting = true;
    try {
      const ok = await this.biometricService.verify();
      if (ok) {
        this.locked.next(false);
      }
    } finally {
      this.prompting = false;
    }
  }

  /** Fallback: sai da conta quando o usuário não consegue/quer usar a biometria. */
  logoutFromLock(): void {
    this.locked.next(false);
    this.securityService.invalidate().subscribe();
  }
}
